import { FC, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslate } from 'react-polyglot'
import { Link, useHistory } from 'react-router-dom'
import { TypographyColor } from '@getgo/chameleon-core/next'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { AlertOutlinedIcon, InfoOutlinedIcon, PasskeyOutlinedIcon } from '@getgo/chameleon-icons/react'
import { Link as ChameleonLink, Typography } from '@getgo/chameleon-web-react-wrapper'
import { useLocalStorage } from '@rehooks/local-storage'
import { Analytics, BILLING_ROUTE, SMS_CAMPAIGN_ROUTE } from '@ucc/unified-admin-common'
import concat from 'lodash/concat'
import mixpanel from 'mixpanel-browser'

import { OrganizationContext, TopBarBannerContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import { TopBannerProps } from 'components/contexts/TopBarBannerContext'
import config from 'config'
import { useFeatureFlagEnabled, usePasskeyPromotionBanner } from 'hooks'
import useBillingFeatures from 'hooks/useBillingFeatures'
import {
  FEATURE_FLAGS,
  PASSKEY_PROMOTIONS_LEARN_MORE_URL,
  SMS_CAMPAIGN_NUMBER_POOL_APPROVAL_MAILTO,
  SMS_CAMPAIGN_NUMBER_POOL_APPROVAL_URL,
} from 'utils/constants'
import { getProductFamilyName } from 'utils/getProductNames'
import { mixpanelEventNames } from 'utils/mixpanel'

interface TopBarBannerProviderProps {
  children?: ReactNode
}

const TopBarBannerProvider: FC<TopBarBannerProviderProps> = ({ children }) => {
  const t = useTranslate()
  const history = useHistory()
  const [oldDismissedBanners] = useLocalStorage<any[]>(config.TOP_BAR_BANNER_DISMISSED, []) // this is the way the banner was dismissed when the top bar banner only accepted 1 banner at a time.
  const [accountDismissedBanner, setAccountDismissedBanner] = useLocalStorage<{ [key: string]: string[] }>(
    config.ACCOUNT_DISMISSED_BANNER,
    {},
  )
  const { accountKey: selectedAccountKey } = useAccountContext()

  const pbxContext = useContext(OrganizationContext)

  const { isBillingAdmin } = useBillingFeatures()

  const isTryNewEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.tryNew)
  const smsCampaignRegistration = useFeatureFlagEnabled(FEATURE_FLAGS.smsCampaignRegistration)

  const isInPreviewMode: boolean = useMemo(
    () => !pbxContext?.billedByLicense && !pbxContext?.unifiedAdmin && isTryNewEnabled,
    [isTryNewEnabled, pbxContext],
  )
  const showPbxDeactivated = pbxContext?.showPbxDeactivatedBanner

  const { passkeyPromotionBannerIsVisible, onPasskeyBannerClose } = usePasskeyPromotionBanner()

  const isPbxMigrated = useMemo(
    () => !pbxContext?.billedByLicense && !!pbxContext?.unifiedAdmin && isTryNewEnabled,
    [isTryNewEnabled, pbxContext],
  )

  const isAssistToResolveMigration = useMemo(
    () => isBillingAdmin && pbxContext?.productMigrationDetails?.status?.toUpperCase() === 'PENDING',
    [isBillingAdmin, pbxContext?.productMigrationDetails?.status],
  )

  const trackOptions = useCallback(() => {
    Analytics.track(mixpanelEventNames.ASSIST_TO_RESOLVE_BANNER, {
      commerceSystem: pbxContext?.commerceSystems
        ? Object.keys(pbxContext.commerceSystems).sort().join(', ').toUpperCase()
        : '',
    })
  }, [pbxContext?.commerceSystems])

  const topBanner = useMemo(() => {
    if (!selectedAccountKey) {
      return null
    }
    const isBannerDismissed = (id) => {
      return (
        !!accountDismissedBanner && !!selectedAccountKey && !!accountDismissedBanner[selectedAccountKey]?.includes(id)
      )
    }
    const bannersList: TopBannerProps[] = [
      {
        id: 'deactivated-pbx-banner',
        visible: showPbxDeactivated || false,
        message: t('This PBX has been Deactivated'),
        accountKey: selectedAccountKey,
        variant: 'danger',
        icon: <AlertOutlinedIcon />,
        closable: true,
        dismissed: isBannerDismissed('deactivated-pbx-banner'),
      },
      {
        id: 'pbx-upgraded-to-gotoadmin-banner',
        visible: isPbxMigrated && !oldDismissedBanners.includes(selectedAccountKey),
        message: t('Your account was upgraded to the new GoTo Admin.'),
        accountKey: selectedAccountKey,
        closable: true,
        dismissed: isBannerDismissed('pbx-upgraded-to-gotoadmin-banner'),
      },
      {
        id: 'account-in-preview-mode-banner',
        visible: isInPreviewMode,
        message: t("You're previewing the new GoTo Admin. Changes you make affect your account."),
        accountKey: selectedAccountKey,
        action: (
          <ChameleonLink
            onClick={() => {
              pbxContext?.setShowUpgradeDialog && pbxContext.setShowUpgradeDialog(true)
            }}
          >
            {t('Upgrade now.')}
          </ChameleonLink>
        ),
        closable: true,
        dismissed: isBannerDismissed('account-in-preview-mode-banner'),
      },
      {
        id: 'sms-compliance-industry-regulations-restriction',
        variant: 'danger',
        visible:
          (smsCampaignRegistration &&
            pbxContext?.pbxSmsCampaignDetails?.pbxIsUsingSms &&
            pbxContext?.pbxSmsCampaignDetails?.pbxNeedsACampaignToUseSms &&
            pbxContext?.pbxSmsCampaignDetails?.userIsAddOnAdmin &&
            !pbxContext?.pbxSmsCampaignDetails?.pbxHasACampaign) ||
          false,
        message: t(
          `US carriers will block all unregistered SMS. Don't wait - complete SMS registration immediately to avoid disruption to your service.`,
        ),
        accountKey: selectedAccountKey,
        closable: true,
        dismissed: isBannerDismissed('sms-compliance-industry-regulations-restriction'),
        action: (
          <ChameleonLink
            onClick={() => {
              mixpanel.track(mixpanelEventNames.SMS_CAMPAIGN, {
                Action: 'Register SMS Compliance from banner',
                selectedAccountKey,
              })
              history.push(`/${selectedAccountKey}/${SMS_CAMPAIGN_ROUTE}`)
            }}
          >
            {t('Register now.')}
          </ChameleonLink>
        ),
      },
      {
        id: 'sms-compliance-industry-action-required',
        visible: (smsCampaignRegistration && pbxContext?.pbxSmsCampaignDetails?.pbxSmsActionRequired) || false,
        variant: 'danger',
        icon: <AlertOutlinedIcon />,
        message: t(
          `Action Required: Your SMS campaign was rejected and requires updates to meet compliance standards. Please update the campaign and resubmit to avoid service disruptions.`,
        ),
        accountKey: selectedAccountKey,
        closable: true,
        dismissed: isBannerDismissed('sms-compliance-industry-action-required'),
        action: (
          <ChameleonLink
            onClick={() => {
              mixpanel.track(mixpanelEventNames.SMS_CAMPAIGN, {
                Action: 'Action required SMS Compliance from banner',
                selectedAccountKey,
              })
              history.push(`/${selectedAccountKey}/${SMS_CAMPAIGN_ROUTE}`)
            }}
          >
            {t('Update now.')}
          </ChameleonLink>
        ),
      },
      {
        id: 'sms-compliance-industry-waiting-customer-answer',
        visible:
          (smsCampaignRegistration && pbxContext?.pbxSmsCampaignDetails?.pbxHasSubIdWaitingCustomerAnswer) || false,
        variant: 'danger',
        icon: <AlertOutlinedIcon />,
        message: (
          <Typography
            dangerouslySetInnerHTML={{
              __html: t(
                `Action Required: Number pool approval is missing. Fill out the <a style='text-decoration: none; color: %{linkColor}' href='%{smsFormUrl}' target='_blank'>required form</a> and send it to <a style='text-decoration: none; color: %{linkColor}' href='mailto:%{smsMailTo}'>numbers@goto.com</a> to avoid service disruptions.`,
                {
                  linkColor: tokens.typeColorDefault,
                  smsFormUrl: SMS_CAMPAIGN_NUMBER_POOL_APPROVAL_URL,
                  smsMailTo: SMS_CAMPAIGN_NUMBER_POOL_APPROVAL_MAILTO,
                },
              ),
            }}
            variant="body-small"
            color="type-color-inverted"
          />
        ),
        accountKey: selectedAccountKey,
        closable: true,
        dismissed: isBannerDismissed('sms-compliance-industry-waiting-customer-answer'),
      },
      {
        id: 'assist-to-resolve-migration-banner',
        variant: 'marketing',
        visible: isAssistToResolveMigration,
        message: t(
          'You can access GoToAssist for the next %{expiringDays} days. Make sure to import your data to %{resolveName} during this time.',
          {
            resolveName: getProductFamilyName('gotoresolve'),
            expiringDays: pbxContext?.productMigrationDetails?.daysLeft,
          },
        ),
        accountKey: selectedAccountKey,
        action: (
          <Link
            onClick={trackOptions}
            style={{
              textDecoration: 'none',
              padding: '0.25rem 0.75rem',
              boxShadow: 'inset 0 0 0 1px var(--goto-border-color-default)',
              borderRadius: '4px',
            }}
            to={`/${selectedAccountKey}/${BILLING_ROUTE}/subscriptions/resolve/opt-out-migration`}
            data-testid="product-migration-banner-options"
          >
            <Typography color={'interactive-color-persistent-default' as TypographyColor} variant="button-small">
              {t('Options')}
            </Typography>
          </Link>
        ),
        closable: false,
        icon: <InfoOutlinedIcon size="21px" />,
        dismissed: isBannerDismissed('assist-to-resolve-migration-banner'),
      },
      {
        id: 'passkey-promotion-banner',
        visible: passkeyPromotionBannerIsVisible,
        onClose: onPasskeyBannerClose,
        variant: 'marketing',
        icon: <PasskeyOutlinedIcon />,
        message: t(`Use a passkey to access GoTo in a more convenient and secure way.`),
        closable: true,
        dismissed: isBannerDismissed('passkey-promotion-banner'),
        action: (
          <ChameleonLink href={PASSKEY_PROMOTIONS_LEARN_MORE_URL} target="_blank">
            {t('Learn more')}
          </ChameleonLink>
        ),
      },
    ]
    const topBannerfiltered: TopBannerProps[] = bannersList.filter((banner) => banner.visible && !banner.dismissed)
    return topBannerfiltered[0]
  }, [
    selectedAccountKey,
    pbxContext,
    smsCampaignRegistration,
    t,
    showPbxDeactivated,
    isPbxMigrated,
    oldDismissedBanners,
    isInPreviewMode,
    accountDismissedBanner,
    history,
    isAssistToResolveMigration,
    trackOptions,
    passkeyPromotionBannerIsVisible,
    onPasskeyBannerClose,
  ])

  return (
    <TopBarBannerContext.Provider
      value={{
        topBanner,
        dismissBanner: () => {
          if (selectedAccountKey && topBanner) {
            const updatedDismissedBanners = {
              ...accountDismissedBanner,
              [selectedAccountKey]: concat(accountDismissedBanner[selectedAccountKey] || [], topBanner.id),
            }
            setAccountDismissedBanner(updatedDismissedBanners)
            topBanner?.onClose && topBanner.onClose()
          }
        },
      }}
    >
      {children}
    </TopBarBannerContext.Provider>
  )
}

export default TopBarBannerProvider
